"use client";

import { useState } from 'react';
import { notFound } from 'next/navigation';
import { blogs } from '@/cms/blogs';
import { events } from '@/cms/events';
import { case_studies } from '@/cms/case-studies';
import { add_blocks_metadata } from '@/features/editor/utils/content-blocks';
import { ContentEditor } from '@/features/editor/components/ContentEditor';
import { ContentItem } from '@/types/cms';
import {
    ContentWrapperTocSubject,
    DefaultContentWrapperLayout,
    getDisplayBlocks,
} from './ContentWrapperVariants';

interface ContentWrapperProps {
    slug: string;
    content_type: 'blog' | 'event' | 'case-study';
}

export default function ContentWrapper({ slug, content_type }: ContentWrapperProps) {
    // Get content from appropriate CMS based on type
    const getInitialContent = () => {
        switch (content_type) {
            case 'blog':
                return blogs[slug];
            case 'event':
                return events[slug];
            case 'case-study':
                return case_studies[slug];
            default:
                return null;
        }
    };

    const initialContent = getInitialContent();

    if (!initialContent) {
        notFound();
    }

    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState<ContentItem>(initialContent);

    const handleContentChange = (newContent: ContentItem['contentBlocks']) => {
        setContent(prev => ({ ...prev, contentBlocks: newContent }));
    };

    const handleMetadataChange = (newMetadata: Partial<ContentItem>) => {
        setContent(prev => ({ ...prev, ...newMetadata }));
    };

    // Convert content blocks to the format expected by the existing TableOfContents component
    const getTocSubject = (): ContentWrapperTocSubject => {
        const blocksWithMetadata = add_blocks_metadata(getDisplayBlocks(content));

        const contentSections = blocksWithMetadata
            .filter(block => block.type === 'heading' && block.level === 2)
            .map((block) => ({
                heading: "text" in block ? block.text : "",
                id: block.id
            }))
            .filter(section => section.heading.length > 0);

        return {
            contentSections
        };
    };

    // Get display names for different content types
    const getContentTypeDisplayName = () => {
        switch (content_type) {
            case 'blog':
                return 'Blog';
            case 'event':
                return 'Event';
            case 'case-study':
                return 'Case Study';
            default:
                return 'Content';
        }
    };

    if (isEditing) {
        return (
            <div className="min-h-screen bg-gray-50">
                <ContentEditor
                    content={content.contentBlocks}
                    onChange={handleContentChange}
                    contentMetadata={content}
                    onMetadataChange={handleMetadataChange}
                />

                {/* Floating Action Buttons */}
                <div className="fixed bottom-8 right-8 flex flex-col gap-3 z-50">
                    <button
                        onClick={() => setIsEditing(false)}
                        className="px-6 py-3 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-full shadow-lg font-semibold transition-colors text-lg"
                    >
                        Preview Mode
                    </button>
                    <button
                        onClick={() => {
                            console.log(`${getContentTypeDisplayName()} Content:`, content);
                            alert(`${getContentTypeDisplayName()} content logged to console!`);
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-full shadow-lg font-semibold hover:bg-green-700 transition-colors text-lg"
                    >
                        Export JSON
                    </button>
                </div>
            </div>
        );
    }

    const tocSubject = getTocSubject();

    return <DefaultContentWrapperLayout content={content} tocSubject={tocSubject} />;
}
